import type { EffectsProp } from 'remotion';
import { xyTranslate } from '@remotion/effects/translate';
import { scale } from '@remotion/effects/scale';
import { zoomBlur } from '@remotion/effects/zoom-blur';
import { exposure } from '@remotion/effects/exposure';
import { chromaticAberration } from '@remotion/effects/chromatic-aberration';
import { noiseDisplacement } from '@remotion/effects/noise-displacement';
import { scanlines } from '@remotion/effects/scanlines';
import { noise } from '@remotion/effects/noise';
import { saturation } from '@remotion/effects/saturation';
import { grayscale } from '@remotion/effects/grayscale';
import { vignette } from '@remotion/effects/vignette';
import { blur } from '@remotion/effects/blur';
import { lut } from '@remotion/effects/lut';
import { scaledBlurDefinition } from '../buildVideoEffects';
import { lutCubeContent } from '../cube';
import { getPresetLutData } from '../presets';
import type { LutPresetId } from '../types';
import { DYNAMIC_EFFECT_IDS, type EffectId } from '../sections';
import { createRandom } from './seededRandom';
import { pixelScaled, withFrameAspect } from './pixelScaled';

export type EffectBuildContext = {
  /** Eased intensity, 0..1. */
  k: number;
  params: { speed?: number; frequency?: number };
  localTime: number;
  duration: number;
  frame: number;
  seed: number;
  /** Composition width / height: pixel params scale to the visible frame. */
  frameAspect?: number;
};
export type EffectLibraryEntry = {
  id: EffectId;
  category: 'dynamic' | 'look';
  labelKey: string;
  build: (ctx: EffectBuildContext) => EffectsProp;
};
type Descriptor = EffectsProp[number];

// Pixel-param definitions, created once (stable identity).
const translateDef = pixelScaled(xyTranslate({ x: 0, y: 0 }).definition as never, ['x', 'y'] as never);
const zoomBlurDef = pixelScaled(zoomBlur({ amount: 0 }).definition as never, ['amount'] as never);
const aberrationDef = pixelScaled(chromaticAberration({ amount: 0 }).definition as never, ['amount'] as never);
const displacementDef = pixelScaled(
  noiseDisplacement({ center: [0.5, 0.5], radius: 0.3 }).definition as never,
  ['strength', 'grainSize'] as never,
);
const scanlinesDef = pixelScaled(scanlines({}).definition as never, ['spacing', 'thickness', 'offset'] as never);

const frac = (x: number) => x - Math.floor(x);

/** Peak shake shift as a fraction of the 1080 px reference width (intensity 100). */
export const SHAKE_MAX_AMPLITUDE = 0.025;
const WORST_ASPECT = 16 / 9; // widest export aspect (landscape)

/** Centre scale that hides a translate (translate first, then scale): an axis
 *  shifted by a fraction d of its length stays covered iff s ≥ 1 / (1 − 2d).
 *  `ax`/`ay` are the peak x/y shifts as fractions of the 1080 px reference.
 *  Translation is pixel-scaled by the VISIBLE width V = min(W, H·frameAspect)
 *  (pixelScaled), so on the source: x shift / W = ax·V/W ≤ ax, and
 *  y shift / H = ay·V/H ≤ ay·frameAspect ≤ ay·16/9, because every export aspect
 *  is at most 16:9 — for ANY source aspect, ultra-wide 2.39:1 and 32:9 included.
 *  Covering the whole source covers every crop window inside it. 2.2 = 2 × a
 *  1.1 safety margin. Shake passes both axes; glitch shifts horizontally only
 *  (ay = 0, ~6.5% zoom instead of ~12%). */
export function shakeCoverScale(ax: number, ay: number): number {
  return Math.max(1 / (1 - 2.2 * ax), 1 / (1 - 2.2 * WORST_ASPECT * ay));
}

function shakeLayers(dx: number, dy: number, ax: number, ay: number, frameAspect: number | undefined): Descriptor[] {
  return [
    withFrameAspect(xyTranslate({ x: dx, y: dy }), translateDef, frameAspect),
    scale({ scale: shakeCoverScale(ax, ay) }),
  ];
}

/** LUT strength steps. An eased section would otherwise rebuild and re-upload
 *  a 17³ cube on every ramp frame and could evict the base LUT from the LUT
 *  cache; 1/32 steps are visually smooth and repeat across ramps. A section
 *  the export gate counts (intensity > 0) must also render, so the smallest
 *  live strength is one step. */
const LUT_STEPS = 32;
const lookLut = (preset: LutPresetId) => ({ k }: EffectBuildContext): EffectsProp => {
  const strength = k > 0 ? Math.max(1 / LUT_STEPS, Math.round(k * LUT_STEPS) / LUT_STEPS) : 0;
  return strength <= 0 ? [] : [lut({ content: lutCubeContent(getPresetLutData(preset), strength) })];
};

const BUILDERS: Record<EffectId, (ctx: EffectBuildContext) => EffectsProp> = {
  shake: ({ k, params, localTime, seed, frameAspect }) => {
    if (k <= 0) return [];
    const speed = params.speed ?? 1;
    const rnd = createRandom(seed);
    const [p1, p2, p3, p4] = [rnd(), rnd(), rnd(), rnd()].map((r) => r * Math.PI * 2);
    const a = SHAKE_MAX_AMPLITUDE * k; // fraction of the 1080 px width
    const w = (f: number, p: number) => Math.sin(2 * Math.PI * f * speed * localTime + p);
    const dx = a * 1080 * (0.6 * w(3, p1) + 0.4 * w(7.3, p2));
    const dy = a * 1080 * (0.6 * w(2.7, p3) + 0.4 * w(6.1, p4));
    return shakeLayers(dx, dy, a, a, frameAspect);
  },
  'zoom-punch': ({ k, params, localTime, frameAspect }) => {
    if (k <= 0) return [];
    const p = Math.exp(-6 * frac(localTime * (params.speed ?? 1.5)));
    return [
      scale({ scale: 1 + 0.15 * k * p }),
      withFrameAspect(zoomBlur({ amount: 40 * k * p }), zoomBlurDef, frameAspect),
    ];
  },
  flash: ({ k, params, localTime }) => {
    if (k <= 0) return [];
    const p = Math.exp(-10 * frac(localTime * (params.frequency ?? 2)));
    return [exposure({ stops: 3 * k * p })];
  },
  glitch: ({ k, params, localTime, seed, frameAspect }) => {
    if (k <= 0) return [];
    const f = params.frequency ?? 4;
    const burst = Math.floor(localTime * f);
    const rnd = createRandom(seed ^ Math.imul(burst + 1, 0x9e3779b1));
    const on = rnd() > 0.35 && frac(localTime * f) < 0.5;
    if (!on) return [];
    const shift = (rnd() - 0.5) * 60 * k; // px at 1080
    const a = Math.abs(shift) / 1080;
    return [
      withFrameAspect(chromaticAberration({ amount: 18 * k, angle: rnd() * 360 }), aberrationDef, frameAspect),
      ...shakeLayers(shift, 0, a, 0, frameAspect),
      withFrameAspect(noiseDisplacement({ center: [rnd(), rnd()], radius: 0.25, strength: 40 * k, grainSize: 8, seed: burst }), displacementDef, frameAspect),
    ];
  },
  'rgb-split': ({ k, frameAspect }) => (k <= 0 ? [] : [withFrameAspect(chromaticAberration({ amount: 14 * k, angle: 0 }), aberrationDef, frameAspect)]),
  'focus-pull': ({ k, localTime, duration, frameAspect }) => {
    if (k <= 0) return [];
    const r = 12 * k * (1 - Math.sin(Math.PI * Math.min(1, Math.max(0, localTime / duration))));
    if (r <= 0.01) return [];
    return [withFrameAspect(blur({ radius: r }), scaledBlurDefinition, frameAspect)];
  },
  strobe: ({ k, params, localTime }) => {
    if (k <= 0) return [];
    const on = frac(localTime * (params.frequency ?? 8)) < 0.5;
    return [exposure({ stops: on ? 1.5 * k : -0.5 * k })];
  },
  vhs: ({ k, localTime, frame, frameAspect }) => (k <= 0 ? [] : [
    withFrameAspect(scanlines({ amount: 0.35 * k, spacing: 4, thickness: 1.5, offset: (localTime * 60) % 4 }), scanlinesDef, frameAspect),
    noise({ amount: 0.18 * k, seed: frame }),
    withFrameAspect(chromaticAberration({ amount: 4 * k, angle: 0 }), aberrationDef, frameAspect),
    saturation({ amount: 1 - 0.3 * k }),
  ]),
  'look-warm': lookLut('warm'),
  'look-cool': lookLut('cool'),
  'look-cinema': lookLut('cinema'),
  'look-vintage': lookLut('vintage'),
  'look-vivid': lookLut('vivid'),
  'look-mono': lookLut('mono'),
  bw: ({ k }) => (k <= 0 ? [] : [grayscale({ amount: k })]),
  grain: ({ k, frame }) => (k <= 0 ? [] : [noise({ amount: 0.3 * k, seed: frame })]),
  vignette: ({ k }) => (k <= 0 ? [] : [vignette({ amount: k })]),
  blur: ({ k, frameAspect }) => (k <= 0 ? [] : [withFrameAspect(blur({ radius: 12 * k }), scaledBlurDefinition, frameAspect)]),
};

export const EFFECT_LIBRARY = Object.fromEntries(
  (Object.keys(BUILDERS) as EffectId[]).map((id) => [id, {
    id,
    category: (DYNAMIC_EFFECT_IDS as readonly string[]).includes(id) ? 'dynamic' : 'look',
    labelKey: `videoEffects.sections.library.${id}`,
    build: BUILDERS[id],
  }]),
) as Record<EffectId, EffectLibraryEntry>;
